import React from 'react';
import styles from './UserVerificationWidget.module.less';
import { Icon, Message } from 'semantic';
import { useUser } from 'contexts/user';

import ModalTriggerButton from 'components/modal/TriggerButton';
import VerifyEmail from './VerifyEmail';
import VerifyMobile from './VerifyMobile';
import { Layout } from 'components/Layout';
import { User } from 'types/user';
import { formatPhone } from 'utils/formatting';
import { useTranslation, TFunction } from 'react-i18next';
import { useAccountPaymentInstruments } from 'contexts/UserPaymentInstruments';
import { Button } from 'semantic-ui-react';
import { useAccount } from 'contexts/account';
import { useEnabledPaymentMethods } from 'contexts/enabledPaymentMethods';

import { useFeatures } from 'contexts/features';
import { Provider } from 'types/provider';

function getMessage(
  user: User,
  provider: Provider,
  hasPhoneNumber: boolean,
  t: TFunction<'translation', undefined>
) {
  const { hasFeature } = useFeatures();

  const enabledVerifications = provider.enableVerifications || [];

  const hasVerifiedEmail =
    (hasFeature('mobile_verification') ||
      enabledVerifications.includes('email')) &&
    user?.emailVerifiedAt;

  const hasVerifiedPhoneNumber =
    (hasFeature('mobile_verification') ||
      enabledVerifications.includes('phone')) &&
    user?.contact?.phoneNoVerifiedAt;

  const { accountPaymentInstruments } = useAccountPaymentInstruments();
  const { isPayBridgeEnabled } = useEnabledPaymentMethods();

  const { account } = useAccount();

  const hasVerifiedBankAccount = (accountPaymentInstruments ?? []).length > 0;

  const showVerifyBankAccountBottom =
    isPayBridgeEnabled &&
    account?.billing?.paymentMethod !== 'manual' &&
    !hasVerifiedBankAccount;

  // Only autopay users, who are in the supported countries should see the verify bank account bottom
  if (showVerifyBankAccountBottom) {
    if (!hasVerifiedEmail && !hasVerifiedPhoneNumber && hasPhoneNumber) {
      return t(
        'userVerfification.unverifiedPhoneAndEmailAndBankAccount',
        'User verification required. Please verify your phone number {{phone}} & e-mail & bank account.',
        {
          phone: formatPhone(
            user.contact?.phoneNo,
            user.contact?.phoneCountryCode
          ),
        }
      );
    }

    if (!hasVerifiedPhoneNumber && hasVerifiedEmail && hasPhoneNumber) {
      return t(
        'userVerfification.unverifiedPhoneAndBankAccount',
        'User verification required. Please verify your phone number {{phone}} & bank account.',
        {
          phone: formatPhone(
            user.contact?.phoneNo,
            user.contact?.phoneCountryCode
          ),
        }
      );
    }

    if (!user.emailVerifiedAt) {
      return t(
        'userVerfification.unverifiedEmailAndBankAccount',
        'User verification required. Please verify e-mail & bank account.'
      );
    }
  } else {
    if (!hasVerifiedEmail && !hasVerifiedPhoneNumber && hasPhoneNumber) {
      return t(
        'userVerfification.unverifiedPhoneAndEmail',
        'User verification required. Please verify your phone number {{phone}} & e-mail.',
        {
          phone: formatPhone(
            user.contact?.phoneNo,
            user.contact?.phoneCountryCode
          ),
        }
      );
    }

    if (!hasVerifiedPhoneNumber && hasVerifiedEmail && hasPhoneNumber) {
      return t(
        'userVerfification.unverifiedPhone',
        'User verification required. Please verify your phone number {{phone}}',
        {
          phone: formatPhone(
            user.contact?.phoneNo,
            user.contact?.phoneCountryCode
          ),
        }
      );
    }

    if (!user.emailVerifiedAt) {
      return t(
        'userVerfification.unverifiedEmail',
        'User verification required. Please verify e-mail.'
      );
    }
  }
  return false;
}

export default function UserVerificationWidget() {
  const { t } = useTranslation();
  const { user, provider } = useUser();

  const { account } = useAccount();

  if (!user || !provider) return null;

  const hasPhoneNumber = Boolean(
    user?.contact?.phoneNo && user?.contact?.phoneCountryCode
  );
  const message = getMessage(user, provider, hasPhoneNumber, t);

  const { accountPaymentInstruments } = useAccountPaymentInstruments();
  const { isPayBridgeEnabled } = useEnabledPaymentMethods();

  const hasVerifiedBankAccount = (accountPaymentInstruments ?? []).length > 0;

  const showVerifyBankAccountBottom =
    account &&
    isPayBridgeEnabled &&
    account?.billing?.paymentMethod !== 'manual' &&
    !hasVerifiedBankAccount;

  // there is nothing to verify return null
  if (!message) return null;

  return (
    <Message info className={styles.container}>
      <div style={{ flex: 1 }}>
        <Layout horizontal center>
          <Icon name="user-shield" size="big" style={{ marginRight: '1em' }} />
          <div style={{ flex: 1, minWidth: '200px' }}>{message}</div>
        </Layout>
      </div>

      <div className={styles.buttons}>
        {!user.contact?.phoneNoVerifiedAt && hasPhoneNumber && (
          <ModalTriggerButton basic modalSize="tiny" modal={<VerifyMobile />}>
            {t('common.verifyPhoneNumber', 'Verify phone number')}
            {' ->'}
          </ModalTriggerButton>
        )}
        {!user.emailVerifiedAt && (
          <ModalTriggerButton basic modalSize="tiny" modal={<VerifyEmail />}>
            {t('common.verifyEmail', 'Verify e-mail')}
            {' ->'}
          </ModalTriggerButton>
        )}
        {showVerifyBankAccountBottom && (
          <Button
            basic
            onClick={() => {
              if (account.type === 'organization') {
                // redirect to billing page
                window.location.href = '/settings/account/organization/billing';
                return;
              } else {
                // redirect to billing page
                window.location.href = '/settings/account/individual/billing';
              }
            }}>
            {t('common.verifyBankAccount', 'Verify bank account')}
            {' ->'}
          </Button>
        )}
      </div>
    </Message>
  );
}
